/**
 * patcher.js — Utility generica per method wrapping (monkey-patching controllato).
 *
 * Wrappa un metodo su un oggetto con un hook `after` che riceve il risultato
 * e gli argomenti originali. Gestisce metodi sync e async (Promise).
 * Non traccia operazioni fallite (l'hook `after` non viene chiamato su errore).
 *
 * @param {object} target     — L'oggetto che contiene il metodo (es. Alpine.store('cart'))
 * @param {string} methodName — Nome del metodo da wrappare (es. 'addItem')
 * @param {object} hooks
 * @param {Function} [hooks.before]  — Chiamato prima del metodo, con (args): serve a
 *                                     fotografare uno stato che il metodo distruggerà
 *                                     (es. l'item che sta per uscire dal carrello).
 * @param {Function} [hooks.after]   — Chiamato dopo il metodo, con (result, args)
 * @param {Function} [hooks.onError] — Chiamato su errore, con (error, args). L'errore viene ri-lanciato.
 */
export function patchMethod(target, methodName, { before, after, onError }) {
    const original = target[methodName];
    if (typeof original !== 'function') return;

    target[methodName] = function (...args) {
        if (before) {
            try { before(args); } catch { /* tracking silenzioso */ }
        }

        let result;
        try {
            result = original.call(this, ...args);
        } catch (err) {
            if (onError) {
                try { onError(err, args); } catch { /* tracking silenzioso */ }
            }
            throw err;
        }

        // Gestione metodi async (Promise)
        if (result && typeof result.then === 'function') {
            return result.then(
                (value) => {
                    if (after) {
                        try { after(value, args); } catch { /* tracking silenzioso */ }
                    }
                    return value;
                },
                (err) => {
                    if (onError) {
                        try { onError(err, args); } catch { /* tracking silenzioso */ }
                    }
                    throw err;
                }
            );
        }

        // Metodo sincrono
        if (after) {
            try { after(result, args); } catch { /* tracking silenzioso */ }
        }
        return result;
    };
}
